import "./ClassicyBootSequence.scss";
import {
	type CSSProperties,
	type FC as FunctionalComponent,
	type ReactNode,
	useState,
} from "react";
import { ClassicyIcons } from "@/SystemFolder/ControlPanels/AppearanceManager/ClassicyIcons";
import { ClassicyStartupScreen } from "@/SystemFolder/SystemResources/Boot/ClassicyStartupScreen";
import {
	hasShownStartupScreenThisSession,
	markStartupScreenShownThisSession,
} from "@/SystemFolder/SystemResources/Boot/ClassicyStartupScreenSession";

interface ClassicyBootSequenceProps {
	startupScreen?: boolean;
	startupDuration?: number;
	preBootScreen?: (powerOn: () => void) => ReactNode;
	/** Overrides the logo image on the default startup screen. Ignored when `bootScreen` is supplied. */
	startupLogo?: string;
	/** Overrides the wordmark text on the default startup screen. Ignored when `bootScreen` is supplied. */
	startupWordmark?: string;
	/**
	 * Replaces the default startup screen entirely. Rendered as-is in place of
	 * `ClassicyStartupScreen` — it owns its own visuals, timing, and (if
	 * desired) session-once gating via the exported
	 * `hasShownStartupScreenThisSession`/`markStartupScreenShownThisSession`
	 * helpers. Still subject to the `startupScreen` gate.
	 */
	bootScreen?: ReactNode;
}

/**
 * Orchestrates the boot phases. When a preBootScreen is supplied and the
 * splash has not run this session, shows a "power on" overlay first; the
 * consumer's button calls powerOn() to advance to ClassicyStartupScreen
 * (which plays the chime + parade). Gating the chime behind that click also
 * satisfies the browser's autoplay gesture requirement. ClassicyStartupScreen
 * self-gates on the session flag, so a reload before power-on replays the
 * overlay, and a reload after boot skips everything.
 */
export const ClassicyBootSequence: FunctionalComponent<
	ClassicyBootSequenceProps
> = ({
	startupScreen = true,
	startupDuration = 4000,
	preBootScreen,
	startupLogo,
	startupWordmark,
	bootScreen,
}) => {
	const [phase, setPhase] = useState<"powerOn" | "startup">(() =>
		preBootScreen && !hasShownStartupScreenThisSession()
			? "powerOn"
			: "startup",
	);

	const powerOn = () => {
		// When there is no startup screen to mount, ClassicyStartupScreen never
		// runs its session-marking effect — so mark it here, or the power-on gate
		// would replay on every reload (breaking once-per-session).
		if (!startupScreen) {
			markStartupScreenShownThisSession();
		}
		setPhase("startup");
	};

	if (phase === "powerOn" && preBootScreen) {
		// Plain overlay: the consumer owns the content (window frame, focus,
		// labels), so we don't advertise modal-dialog semantics we can't back.
		// Hide the desktop behind an opaque white sheet tiled with the halftone
		// pattern (a transparent PNG resolved in JS, handed to SCSS as a var).
		const overlayStyle = {
			"--classicy-preboot-halftone": `url(${ClassicyIcons.ui.halftoneLarge})`,
		} as CSSProperties;
		return (
			<div className="classicyPreBootScreen" style={overlayStyle}>
				{preBootScreen(powerOn)}
			</div>
		);
	}

	if (!startupScreen) return null;
	return (
		bootScreen ?? (
			<ClassicyStartupScreen
				duration={startupDuration}
				logo={startupLogo}
				wordmark={startupWordmark}
			/>
		)
	);
};
